<template>
  <NavBar pageName="Msisdns"></NavBar>

  <!-- Buy Number Dialog -->
  <SelectAvailableMsisdn
    v-if="showBuyNumberDialog"
    @dialogSelectAvailableMsisdnEscapedEvent="closeBuyNumberDialog"
    @msisdnSelected="onMsisdnPurchased"
  />

  <!-- MSISDN Details Popup -->
  <MsisdnDetailsPopup
    v-if="showDetailsPopup"
    :sim-id="selectedSimId"
    :msisdn-id="selectedMsisdnId"
    @close="closeDetailsPopup"
  />

  <!-- Buy/Rent MSISDN Dialog (Single Mode) -->
  <BuyRentMsisdnDialog
    v-if="showBuyRentDialog && !bulkBuyRentMode"
    :msisdn-id="selectedBuyRentMsisdnId"
    :msisdn-number="selectedBuyRentMsisdnNumber"
    @close="closeBuyRentDialog"
    @purchased="onBuyRentPurchased"
  />

  <!-- Buy/Rent MSISDN Dialog (Bulk Mode) -->
  <BuyRentMsisdnDialog
    v-if="showBuyRentDialog && bulkBuyRentMode"
    :msisdn-ids="selectedMsisdnIdsForBulkPurchase"
    @close="closeBuyRentDialog"
    @purchased="onBulkBuyRentPurchased"
  />

  <!-- Payment Popup Dialog -->
  <v-dialog v-model="showPaymentPopup" max-width="450" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-credit-card</v-icon>
        Payment Required
      </v-card-title>
      <v-card-text>
        <p class="mb-4">{{ paymentPopupMessage }}</p>
        <v-btn
          color="primary"
          variant="elevated"
          block
          size="large"
          :href="paymentPopupUrl"
          target="_blank"
          @click="onPaymentPopupPayClick"
        >
          <v-icon start>mdi-open-in-new</v-icon>
          Pay Now
        </v-btn>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" color="red" @click="closePaymentPopup">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Provision SIM Dialog -->
  <v-dialog v-model="showProvisionDialog" max-width="500">
    <v-card>
      <v-card-title>Provizioneaza SIM</v-card-title>
      <v-card-text>
        <p class="mb-4">Selecteaza un IMSI (serial de pe SIM-ul fizic) pentru numarul: <strong>{{ provisioningItem?.msisdn || 'N/A' }}</strong></p>

        <!-- Loading indicator -->
        <div v-if="loadingAvailableImsis && !imsiLoadAttempted" class="text-center py-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2">Se incarca IMSI-urile disponibile...</p>
        </div>

        <!-- No IMSIs available message -->
        <v-alert
          v-else-if="imsiLoadAttempted && noImsisAvailable"
          type="warning"
          class="mb-4"
        >
          <p class="mb-2"><strong>Nu exista IMSI-uri disponibile pentru acest client.</strong></p>
          <p class="text-body-2 mb-3">
            Pentru a putea proviziona acest numar, clientul trebuie sa aiba IMSI-uri alocate si disponibile (cu status 'reserved' in retea).
          </p>
          <v-btn
            color="warning"
            size="small"
            variant="outlined"
            @click="requestImsisFromDistributor"
          >
            Solicita IMSI-uri de la distribuitor
          </v-btn>
        </v-alert>

        <!-- IMSI selector (show when IMSIs are available or after initial load) -->
        <v-autocomplete
          v-else
          v-model="selectedProvisionImsi"
          v-model:search="provisionImsiSearchText"
          :items="availableImsisForProvision"
          :loading="loadingAvailableImsis"
          item-value="id"
          item-title="display_name"
          label="Cauta IMSI/Serial"
          placeholder="Introdu serialul de pe SIM sau alege din lista..."
          clearable
          auto-select-first
          return-object
          @update:search="onProvisionImsiSearch"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props">
              <template v-slot:subtitle>
                IMSI: {{ item.raw.imsi || '-' }}
              </template>
            </v-list-item>
          </template>
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>
                {{ provisionImsiSearchText ? 'Niciun rezultat gasit' : 'Tasteaza pentru a cauta...' }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>

        <!-- Product Code selector -->
        <v-select
          v-if="!noImsisAvailable && selectedProvisionImsi"
          v-model="selectedProductCode"
          :items="productCodesForSelect"
          item-title="display"
          item-value="name"
          label="Selecteaza Product Code"
          :loading="loadingProductCodes"
          :disabled="loadingProductCodes"
          class="mt-4"
          hint="Codul produsului pentru provizionare la MNO"
          persistent-hint
        ></v-select>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeProvisionDialog">Anuleaza</v-btn>
        <v-btn
          v-if="!noImsisAvailable"
          color="success"
          :loading="provisionLoading"
          :disabled="!selectedProvisionImsi || !selectedProductCode"
          @click="executeProvision"
        >
          Provizioneaza
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Reprovision SIM Dialog (for terminated SIMs) -->
  <v-dialog v-model="showReprovisionDialog" max-width="500">
    <v-card>
      <v-card-title>Reprovizioneaza SIM</v-card-title>
      <v-card-text>
        <p class="mb-4">Selecteaza un IMSI (serial de pe SIM-ul fizic) pentru numarul: <strong>{{ reprovisionItem?.msisdn || 'N/A' }}</strong></p>
        <v-alert type="info" variant="tonal" class="mb-4">
          <strong>Nota:</strong> SIM-ul anterior a fost terminat. Se va crea un SIM nou si se va proviziona.
        </v-alert>

        <!-- Loading indicator -->
        <div v-if="loadingReprovisionImsis && !reprovisionImsiLoadAttempted" class="text-center py-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2">Se incarca IMSI-urile disponibile...</p>
        </div>

        <!-- No IMSIs available message -->
        <v-alert
          v-else-if="reprovisionImsiLoadAttempted && noReprovisionImsisAvailable"
          type="warning"
          class="mb-4"
        >
          <p class="mb-2"><strong>Nu exista IMSI-uri disponibile pentru acest client.</strong></p>
          <p class="text-body-2">
            Pentru a putea proviziona acest numar, clientul trebuie sa aiba IMSI-uri alocate si disponibile.
          </p>
        </v-alert>

        <!-- IMSI selector -->
        <v-autocomplete
          v-else
          v-model="selectedReprovisionImsi"
          v-model:search="reprovisionImsiSearchText"
          :items="availableReprovisionImsis"
          :loading="loadingReprovisionImsis"
          item-value="id"
          item-title="display_name"
          label="Cauta IMSI/Serial"
          placeholder="Introdu serialul de pe SIM sau alege din lista..."
          clearable
          auto-select-first
          return-object
          @update:search="onReprovisionImsiSearch"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props">
              <template v-slot:subtitle>
                IMSI: {{ item.raw.imsi || '-' }}
              </template>
            </v-list-item>
          </template>
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>
                {{ reprovisionImsiSearchText ? 'Niciun rezultat gasit' : 'Tasteaza pentru a cauta...' }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>

        <!-- Product Code selector -->
        <v-select
          v-if="!noReprovisionImsisAvailable && selectedReprovisionImsi"
          v-model="selectedReprovisionProductCode"
          :items="reprovisionProductCodesForSelect"
          item-title="display"
          item-value="name"
          label="Selecteaza Product Code"
          :loading="loadingReprovisionProductCodes"
          :disabled="loadingReprovisionProductCodes"
          class="mt-4"
          hint="Codul produsului pentru provizionare la MNO"
          persistent-hint
        ></v-select>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeReprovisionDialog">Anuleaza</v-btn>
        <v-btn
          v-if="!noReprovisionImsisAvailable"
          color="success"
          :loading="reprovisionLoading"
          :disabled="!selectedReprovisionImsi || !selectedReprovisionProductCode"
          @click="executeReprovision"
        >
          Provizioneaza
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="showDeleteDialog" max-width="450">
    <v-card>
      <v-card-title class="text-error">Confirmare stergere</v-card-title>
      <v-card-text>
        <p>Esti sigur ca vrei sa stergi acest serviciu?</p>
        <p class="mt-2"><strong>MSISDN:</strong> {{ deletingItem?.msisdn }}</p>
        <p class="text-caption text-grey mt-2">
          Aceasta actiune va elibera numarul MSISDN si IMSI-ul asociat (daca exista) pentru a fi disponibile din nou.
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showDeleteDialog = false">Anuleaza</v-btn>
        <v-btn
          color="error"
          :loading="deleteLoading"
          @click="executeDeleteService"
        >
          Sterge
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Terminate Confirmation Dialog -->
  <v-dialog v-model="showTerminateDialog" max-width="450">
    <v-card>
      <v-card-title class="text-error">Confirmare terminare serviciu</v-card-title>
      <v-card-text>
        <p>Esti sigur ca vrei sa termini acest serviciu?</p>
        <p class="mt-2"><strong>MSISDN:</strong> {{ terminatingItem?.msisdn }}</p>
        <p class="text-caption text-grey mt-2">
          Aceasta actiune va termina serviciul si va elibera numarul MSISDN si IMSI-ul asociat (daca exista).
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showTerminateDialog = false">Anuleaza</v-btn>
        <v-btn
          color="error"
          :loading="terminateLoading"
          @click="executeTerminateService"
        >
          Termina
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Send Request Dialog (for non-distributor owned MSISDNs) -->
  <v-dialog v-model="showSendRequestDialog" max-width="500">
    <v-card>
      <v-card-title>Solicita numar de telefon</v-card-title>
      <v-card-text>
        <p class="mb-4">
          Numarul <strong>{{ sendRequestItem?.msisdn || 'N/A' }}</strong> este alocat clientului
          <strong>{{ sendRequestItem?.client_name || 'N/A' }}</strong>.
        </p>
        <p class="mb-4 text-body-2">
          Trimite o solicitare proprietarului pentru a exprima interesul de achizitie a acestui numar.
        </p>
        <v-textarea
          v-model="sendRequestMessage"
          label="Mesaj (optional)"
          placeholder="Adauga un mesaj pentru proprietar..."
          rows="3"
          counter
          maxlength="500"
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeSendRequestDialog">Anuleaza</v-btn>
        <v-btn
          color="primary"
          :loading="sendRequestLoading"
          @click="executeSendRequest"
        >
          Trimite solicitare
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Snackbar for messages -->
  <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
    {{ snackbarMessage }}
  </v-snackbar>

  <!-- Create MSISDN Group Dialog -->
  <v-dialog v-model="showCreateGroupDialog" max-width="500">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-folder-plus</v-icon>
        Create MSISDN Group
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="newGroupName"
          label="Group Name"
          required
          density="compact"
          class="mb-3"
        ></v-text-field>
        <v-textarea
          v-model="newGroupDescription"
          label="Description (optional)"
          rows="2"
          density="compact"
        ></v-textarea>
        <div class="text-caption mt-2">
          {{ selectedItems.length }} MSISDNs will be added to this group.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showCreateGroupDialog = false">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="createGroupLoading"
          :disabled="!newGroupName"
          @click="createMsisdnGroupAction"
        >
          Create Group
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Add to MSISDN Group Dialog -->
  <v-dialog v-model="showAddToGroupDialog" max-width="500">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-folder-arrow-right</v-icon>
        Add to Group
      </v-card-title>
      <v-card-text>
        <!-- Toggle between select existing and create new -->
        <div v-if="!showInlineCreateForm">
          <v-select
            v-model="selectedGroupId"
            :items="existingMsisdnGroups"
            item-title="name"
            item-value="id"
            label="Select Existing Group"
            density="compact"
            @click:clear="selectedGroupId = null"
          >
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:append>
                  <v-chip size="x-small">{{ item.raw.member_count }} members</v-chip>
                </template>
              </v-list-item>
            </template>
          </v-select>
          <div class="text-center my-3">
            <v-btn
              variant="text"
              color="primary"
              size="small"
              @click="showInlineCreateForm = true"
            >
              <v-icon size="small" class="mr-1">mdi-plus</v-icon>
              Create new group
            </v-btn>
          </div>
        </div>

        <!-- Inline Create Group Form -->
        <div v-else>
          <div class="d-flex align-center mb-3">
            <v-btn
              icon
              variant="text"
              size="small"
              @click="showInlineCreateForm = false"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="ml-2 font-weight-medium">Create New Group</span>
          </div>
          <v-text-field
            v-model="inlineNewGroupName"
            label="Group Name"
            required
            density="compact"
            class="mb-3"
          ></v-text-field>
          <v-textarea
            v-model="inlineNewGroupDescription"
            label="Description (optional)"
            rows="2"
            density="compact"
            class="mb-3"
          ></v-textarea>
          <ClientSearchFilter
            v-model="inlineNewGroupClientId"
            :show-include-subclients="false"
            client-select-label="Owner Client"
          />
        </div>

        <div class="text-caption mt-2">
          {{ selectedItems.length }} MSISDNs will be added to the {{ showInlineCreateForm ? 'new' : 'selected' }} group.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeAddToGroupDialog">Cancel</v-btn>
        <v-btn
          v-if="!showInlineCreateForm"
          color="primary"
          :loading="addToGroupLoading"
          :disabled="!selectedGroupId"
          @click="addToMsisdnGroup"
        >
          Add to Group
        </v-btn>
        <v-btn
          v-else
          color="primary"
          :loading="inlineCreateGroupLoading"
          :disabled="!inlineNewGroupName"
          @click="inlineCreateGroupAndAdd"
        >
          Create & Add
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-container style="margin-top: 185px;">
    <!-- Header with Buy Number button -->
    <v-row class="mb-4">
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <h2>MSISDN Management</h2>
          <v-tooltip text="Rebuild client hierarchy cache" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" icon="mdi-database-refresh" variant="text" size="small" class="ml-2" :loading="cacheRebuilding" @click="rebuildCache" />
            </template>
          </v-tooltip>
        </div>
        <div class="d-flex gap-2">
          <v-btn color="secondary" variant="outlined" @click="$router.push('/msisdnInventory')">
            <v-icon start>mdi-database-plus</v-icon>
            MSISDN Inventory
          </v-btn>
          <v-btn color="teal" variant="outlined" @click="goToMyMsisdns">
            <v-icon start>mdi-phone-plus</v-icon>
            My Numbers
          </v-btn>
          <v-btn color="indigo" variant="outlined" @click="goToMyMsisdns">
            <v-icon start>mdi-swap-horizontal</v-icon>
            Portabilitate
          </v-btn>
          <v-btn color="primary" @click="openBuyNumberDialog">
            Buy Number
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Filters Card -->
    <v-card class="mb-4 pa-4">
      <!-- Status Filter -->
      <v-row>
        <v-col cols="12">
          <MsisdnStatusButtonFilter v-model="filters.statusMode" :statuses="msisdnStatuses" />
        </v-col>
      </v-row>

      <!-- Network Filter -->
      <v-row v-if="networks.length > 0" class="mt-2">
        <v-col cols="12">
          <span class="font-weight-bold mr-3">Network:</span>
          <v-radio-group
            v-model="filters.network_id"
            row
            density="compact"
            hide-details
            inline
          >
            <v-radio :value="null" label="All"></v-radio>
            <v-radio
              v-for="network in networks"
              :key="network.id"
              :value="network.id"
              :label="network.name"
            ></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>

      <!-- Search and Class Filter -->
      <v-row class="mt-3">
        <v-col cols="12" md="6">
          <div class="position-relative">
            <v-text-field
              v-model="filters.msisdn_like"
              :label="searchByLetters ? 'Search by letters (max 5)' : 'Search by number'"
              :placeholder="searchByLetters ? 'Ex: PIZZA, LOVE, TAXI...' : 'Enter MSISDN to search...'"
              clearable
              dense
              outlined
              hide-details
              :maxlength="searchByLetters ? 5 : 20"
              @keyup.enter="loadMsisdns"
              @input="onVanitySearchInput($event.target.value)"
              @blur="closeVanitySuggestions"
              @focus="searchByLetters && filters.msisdn_like && onVanitySearchInput(filters.msisdn_like)"
              @click="$event.target.select()"
              @dblclick="filters.msisdn_like = ''"
            >
              <template v-slot:append-inner>
                <v-progress-circular
                  v-if="loadingVanitySuggestions"
                  indeterminate
                  size="20"
                  width="2"
                  class="mr-2"
                ></v-progress-circular>
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      v-bind="props"
                      :color="searchByLetters ? 'primary' : 'grey'"
                      @click="searchByLetters = !searchByLetters; vanitySuggestions = []; showVanitySuggestions = false;"
                      style="cursor: pointer"
                    >
                      {{ searchByLetters ? 'mdi-alphabetical' : 'mdi-numeric' }}
                    </v-icon>
                  </template>
                  <span>{{ searchByLetters ? 'Switch to number search' : 'Switch to letter search (vanity)' }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <!-- Vanity Suggestions Dropdown -->
            <v-card
              v-if="showVanitySuggestions && vanitySuggestions.length > 0"
              class="vanity-suggestions-dropdown"
              elevation="8"
            >
              <v-list density="compact">
                <v-list-subheader>Dictionary suggestions (EN/RO)</v-list-subheader>
                <v-list-item
                  v-for="(suggestion, index) in vanitySuggestions"
                  :key="index"
                  @click="selectVanitySuggestion(suggestion)"
                  class="vanity-suggestion-item"
                >
                  <template v-slot:prepend>
                    <v-chip size="x-small" :color="suggestion.language === 'ro' ? 'blue' : 'green'" class="mr-2">
                      {{ suggestion.language.toUpperCase() }}
                    </v-chip>
                  </template>
                  <v-list-item-title>
                    <strong>{{ suggestion.word }}</strong>
                    <span class="text-grey ml-2">= {{ suggestion.digits }}</span>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </div>
          <div v-if="searchByLetters && filters.msisdn_like" class="text-caption mt-1 text-grey">
            Searching for: <strong>{{ lettersToDigits(filters.msisdn_like) }}</strong>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="filters.msisdn_class_id"
            :items="msisdnClassesWithAll"
            item-value="id"
            item-title="name"
            label="MSISDN Class"
            clearable
            dense
            outlined
            hide-details
          ></v-select>
        </v-col>
      </v-row>

      <!-- Client Filter -->
      <v-row class="mt-3">
        <v-col cols="12">
          <ClientSearchFilter
            v-model="filters.client_id"
            v-model:include-subclients="filters.include_subclients"
            client-select-label="Filter by Client"
          />
        </v-col>
      </v-row>

      <!-- Group Filter -->
      <v-row class="mt-3" align="center">
        <v-col cols="12" md="4"></v-col>
        <!-- MSISDN Group Filter (only if client has groups) -->
        <v-col v-if="msisdnGroups.length > 0" cols="12" md="4">
          <v-select
            v-model="filters.msisdn_group_id"
            :items="msisdnGroupsWithAll"
            item-value="id"
            item-title="name"
            label="MSISDN Group"
            clearable
            density="compact"
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="sortBy"
            :items="sortOptions"
            item-value="value"
            item-title="text"
            label="Sort by"
            density="compact"
            hide-details
          ></v-select>
        </v-col>
      </v-row>

      <!-- Action Buttons -->
      <v-row class="mt-4">
        <v-col cols="12" class="d-flex gap-2">
          <v-btn color="primary" @click="loadMsisdns" :loading="loading">
            Search
          </v-btn>
          <v-btn variant="outlined" @click="resetFilters">
            Reset
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            color="success"
            @click="exportCSV"
            :disabled="msisdnsList.length === 0"
          >
            Export CSV
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Search Hint (when no search done yet) -->
    <v-alert v-if="!initialLoadDone && !loading" type="info" variant="tonal" class="mb-4">
      <v-icon class="mr-2">mdi-information</v-icon>
      Introduceti minim 3 caractere in campul de cautare, selectati un client, o clasa, un grup, o retea, sau alegeti un status specific pentru a afisa rezultate.
      Puteti folosi <strong>*</strong> ca wildcard (ex: <strong>0741*123</strong>).
    </v-alert>

    <!-- Results Summary -->
    <v-row v-if="initialLoadDone" class="mb-2">
      <v-col cols="12">
        <span class="text-caption">
          Showing {{ msisdnsList.length }} of {{ totalResults }} results
        </span>
      </v-col>
    </v-row>

    <!-- Selection Toolbar (Sticky) -->
    <v-card v-if="initialLoadDone && selectedItems.length > 0" class="mb-4 pa-3 bg-green-lighten-5 selection-toolbar">
      <div class="d-flex align-center justify-space-between flex-wrap">
        <div class="d-flex align-center gap-2">
          <strong>{{ selectedItems.length }}</strong>
          <span v-if="selectAllMode"> of {{ totalResults }} (all filtered)</span>
          <span v-else> selected</span>
          <v-btn
            v-if="!selectAllMode && totalResults > msisdnsList.length"
            variant="text"
            size="small"
            color="primary"
            :loading="loadingAllIds"
            @click="selectAllFilteredMsisdns"
          >
            Select all {{ totalResults }}
          </v-btn>
          <v-btn variant="text" size="small" @click="clearSelection">Clear</v-btn>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <v-btn size="small" color="success" @click="openBulkBuyRentDialog">
            <v-icon size="small" class="mr-1">mdi-cart</v-icon>
            Buy/Rent Selected
          </v-btn>
          <v-btn size="small" color="primary" @click="openCreateGroupDialog">
            <v-icon size="small" class="mr-1">mdi-folder-plus</v-icon>
            Create Group
          </v-btn>
          <v-btn size="small" variant="outlined" @click="openAddToGroupDialog">
            <v-icon size="small" class="mr-1">mdi-folder-arrow-right</v-icon>
            Add to Group
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Results Table -->
    <v-card v-if="initialLoadDone || loading">
      <v-data-table
        v-model="selectedItems"
        :headers="tableHeaders"
        :items="msisdnsList"
        :loading="loading"
        :items-per-page="perPage"
        :row-props="getRowProps"
        v-model:sort-by="tableSortBy"
        @update:sort-by="onTableSortChange"
        show-select
        item-value="msisdn_id"
        class="elevation-1"
      >
        <template v-slot:[`item.msisdn`]="{ item }">
          <span class="font-weight-bold">{{ item.msisdn }}</span>
        </template>

        <template v-slot:[`item.msisdn_status_name`]="{ item }">
          <v-chip
            :color="getMsisdnStatusColor(item.msisdn_status_name)"
            size="small"
            variant="tonal"
          >
            {{ item.msisdn_status_name || 'available' }}
          </v-chip>
        </template>

        <template v-slot:[`item.status_name`]="{ item }">
          <v-chip
            :color="getStatusColor(item.status_name)"
            size="small"
            variant="outlined"
          >
            {{ item.status_name }}
          </v-chip>
        </template>

        <template v-slot:[`item.sim_status_name`]="{ item }">
          <v-chip
            :color="getSimStatusColor(item.sim_status_name)"
            size="small"
            variant="outlined"
            :class="item.sim_id ? 'clickable-link' : ''"
            @click.stop="item.imsi_serial ? $router.push({ name: 'sims', query: { search: item.imsi_serial } }) : null"
          >
            <v-icon v-if="item.sim_id" start size="x-small">mdi-sim</v-icon>
            {{ formatSimStatus(item.sim_status_name) }}
          </v-chip>
        </template>

        <template v-slot:[`item.expiration_date`]="{ item }">
          <span :class="isExpiringSoon(item.expiration_date) ? 'text-red' : ''">
            {{ formatDate(item.expiration_date) }}
          </span>
        </template>

        <template v-slot:[`item.client_name`]="{ item }">
          <span
            v-if="item.client_name && item.client_id"
            class="clickable-client"
            @click="navigateToClient(item.client_id)"
            :title="'View client: ' + item.client_name"
          >
            {{ item.client_name }}
          </span>
          <span v-else>{{ item.client_name || '-' }}</span>
        </template>

        <template v-slot:[`item.msisdn_class_name`]="{ item }">
          {{ item.msisdn_class_name || '-' }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            variant="elevated"
            size="x-small"
            color="success"
            class="mr-2"
            @click="openDetailsPopup(item.sim_id, item.msisdn_id)"
          >
            <v-icon size="small" class="mr-1">mdi-sim</v-icon>
            SimInfo
          </v-btn>
          <v-btn
            v-if="item.sim_id"
            variant="text"
            size="x-small"
            color="primary"
            class="mr-1"
            @click="goToSim(item)"
          >
            <v-icon size="small" class="mr-1">mdi-sim</v-icon>
            SIM
          </v-btn>
          <v-btn
            v-if="item.sim_id && item.status_name === 'active'"
            variant="text"
            size="x-small"
            color="teal"
            class="mr-1"
            @click="loadCredit(item)"
          >
            <v-icon size="small" class="mr-1">mdi-cash-plus</v-icon>
            Incarca credit
          </v-btn>
          <!-- Buy/Rent: only show if NOT active CS for msisdn/imsi_msisdn service AND (not allocated OR owner is distributor) AND own network AND not owned by current client -->
          <v-btn
            v-if="!hasActiveMsisdnService(item) && (!item.is_allocated || item.owner_is_distributor) && item.own_network !== false && item.client_id !== client_id"
            variant="text"
            size="small"
            :color="item.msisdn_status_name === 'available' ? 'success' : 'warning'"
            @click="item.msisdn_status_name === 'available' ? openBuyRentDialog(item) : goToUnderConstruction(item)"
          >
            Buy/Rent
          </v-btn>
          <!-- Porteaza: show for other network MSISDNs with active service and no active port request -->
          <v-chip
            v-if="item.has_active_port_request"
            size="small"
            color="warning"
            variant="tonal"
          >
            <v-icon start size="small">mdi-swap-horizontal</v-icon>
            Portare in curs
          </v-chip>
          <v-btn
            v-else-if="item.own_network === false && hasActiveMsisdnService(item) && item.client_id !== client_id"
            variant="text"
            size="small"
            color="primary"
            @click="openPortabilityDialog(item)"
          >
            Porteaza
          </v-btn>
          <!-- Send Request: show if MSISDN is allocated to a non-distributor and not owned by current client -->
          <v-btn
            v-if="!hasActiveMsisdnService(item) && item.is_allocated && !item.owner_is_distributor && item.client_id !== client_id"
            variant="text"
            size="small"
            color="info"
            @click="openSendRequestDialog(item)"
          >
            Send Request
          </v-btn>
          <!-- Terminate: show if HAS active CS for msisdn/imsi_msisdn service -->
          <v-btn
            v-if="hasActiveMsisdnService(item)"
            variant="text"
            size="small"
            color="error"
            @click="confirmTerminateService(item)"
          >
            Termina
          </v-btn>
          <v-btn
            v-if="item.sim_status_name === 'not_provisioned' && item.status_name === 'active'"
            variant="text"
            size="small"
            color="warning"
            @click="openProvisionDialog(item)"
          >
            Provizioneaza
          </v-btn>
          <!-- Reprovision for terminated SIMs -->
          <v-btn
            v-if="item.sim_status_name === 'terminated' && item.status_name === 'active'"
            variant="text"
            size="small"
            color="success"
            @click="openReprovisionDialog(item)"
          >
            Provizioneaza
          </v-btn>
          <v-btn
            v-if="item.status_name === 'pending_payment'"
            variant="text"
            size="small"
            color="warning"
            @click="payForService(item)"
            :loading="payingServiceId === item.client_service_id"
          >
            Pay
          </v-btn>
          <v-btn
            v-if="item.status_name === 'pending_payment'"
            variant="text"
            size="small"
            color="error"
            @click="confirmDeleteService(item)"
          >
            Sterge
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Pagination -->
    <v-row class="mt-4" v-if="totalPages > 1">
      <v-col cols="12" class="d-flex justify-center">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          @update:model-value="onPageChange"
        ></v-pagination>
      </v-col>
    </v-row>

    <!-- Portability Request Dialog -->
    <CreatePortabilityRequestDialog
      v-if="showPortabilityDialog"
      :token="token"
      :msisdn="portabilityMsisdn"
      @close="showPortabilityDialog = false"
      @created="onPortRequestCreated"
    />
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import useGetLoggedIn from "@/composables/useGetLoggedIn"
import {log} from "@/composables/useLog";
import {getDisplaySettings} from "@/composables/useDisplaySettingsAPI";
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import SelectAvailableMsisdn from "@/components/SelectAvailableMsisdn.vue";
import MsisdnDetailsPopup from "@/components/MsisdnDetailsPopup.vue";
import BuyRentMsisdnDialog from "@/components/BuyRentMsisdnDialog.vue";
import CreatePortabilityRequestDialog from "@/components/CreatePortabilityRequestDialog.vue";
import { payOrder } from "@/composables/useMsisdnPurchaseAPI";
import { getAllocatedMsisdns, deleteClientService, terminateClientService, sendMsisdnInterestRequest } from "@/composables/useGetAllocatedMsisdnsAPI";
import { getMsisdnsClasses } from "@/composables/useGetMsisdnsAPIs";
import { provisionSim, getAvailableImsisForProvision, reprovisionTerminatedSim } from "@/composables/useGetSimsAPI";
import { getProductCodesForProvisioning } from "@/composables/useProductsAPI";
import { lettersToDigits, searchVanityWords } from "@/composables/useVanitySearch";
import { requestImsisFromDistributor } from "@/composables/useTodosAPI";
import ClientSearchFilter from "@/components/ClientSearchFilter.vue";
import MsisdnStatusButtonFilter from "@/components/MsisdnStatusButtonFilter.vue";
import { getMsisdnGroups, createMsisdnGroup, addMsisdnsToGroup, getAllFilteredMsisdnIds } from "@/composables/useGroupsAPI";
import { getMsisdnStatuses } from "@/composables/useMsisdnStatusesAPI";

export default defineComponent({
  name: 'MsisdnsList',
  components: {
    NavBar,
    SelectAvailableMsisdn,
    MsisdnDetailsPopup,
    BuyRentMsisdnDialog,
    ClientSearchFilter,
    MsisdnStatusButtonFilter,
    CreatePortabilityRequestDialog
  },
  data() {
    return {
      loading: false,
      token: '',
      client_id: store.getters.getUser?.client_id,

      // Dialogs
      showBuyNumberDialog: false,
      showDetailsPopup: false,
      selectedSimId: null,
      selectedMsisdnId: null,

      // Provision dialog
      showProvisionDialog: false,
      provisioningItem: null,
      selectedProvisionImsi: null,
      provisionImsiSearchText: '',
      availableImsisForProvision: [],
      loadingAvailableImsis: false,
      provisionLoading: false,
      provisionSearchTimeout: null,
      noImsisAvailable: false,
      imsiLoadAttempted: false,
      // Product codes for provision
      availableProductCodes: [],
      selectedProductCode: null,
      loadingProductCodes: false,

      // Reprovision dialog (for terminated SIMs)
      showReprovisionDialog: false,
      reprovisionItem: null,
      selectedReprovisionImsi: null,
      reprovisionImsiSearchText: '',
      availableReprovisionImsis: [],
      loadingReprovisionImsis: false,
      reprovisionLoading: false,
      reprovisionSearchTimeout: null,
      noReprovisionImsisAvailable: false,
      reprovisionImsiLoadAttempted: false,
      reprovisionProductCodes: [],
      selectedReprovisionProductCode: null,
      loadingReprovisionProductCodes: false,

      // Delete dialog
      showDeleteDialog: false,
      deletingItem: null,
      deleteLoading: false,

      // Terminate dialog
      showTerminateDialog: false,
      terminatingItem: null,
      terminateLoading: false,

      // Snackbar
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      // Search control - don't load all records on page open
      initialLoadDone: false,

      // Filters
      filters: {
        statusMode: 'all_except_terminated',
        msisdn_like: '',
        msisdn_class_id: null,
        msisdn_group_id: null,
        network_id: null,
        include_subclients: false,
        client_id: null
      },

      // Sorting
      sortBy: 'expiration_date_asc',
      sortOptions: [
        { text: 'Expiration (soonest first)', value: 'expiration_date_asc' },
        { text: 'Expiration (latest first)', value: 'expiration_date_desc' },
        { text: 'MSISDN (A-Z)', value: 'msisdn_asc' },
        { text: 'MSISDN (Z-A)', value: 'msisdn_desc' },
        { text: 'Client Name (A-Z)', value: 'client_name_asc' }
      ],

      // Pagination
      currentPage: 1,
      perPage: 20,
      totalResults: 0,

      // Data
      msisdnsList: [],
      msisdnClasses: [],
      msisdnStatuses: [],
      msisdnGroups: [],
      networks: [],

      // Table headers
      tableHeaders: [
        { title: 'MSISDN', key: 'msisdn', sortable: true },
        { title: 'MSISDN Status', key: 'msisdn_status_name', sortable: true },
        { title: 'Status plata', key: 'status_name', sortable: true },
        { title: 'Status retea', key: 'sim_status_name', sortable: true },
        { title: 'Class', key: 'msisdn_class_name', sortable: true },
        { title: 'Network', key: 'network_name', sortable: true },
        { title: 'Client', key: 'client_name', sortable: true },
        { title: 'Expiration', key: 'expiration_date', sortable: true },
        { title: 'Actions', key: 'actions', sortable: false }
      ],

      // Table sorting state
      tableSortBy: [],

      // Vanity search
      searchByLetters: false,
      vanitySearchTimeout: null,
      vanitySuggestions: [],
      loadingVanitySuggestions: false,
      showVanitySuggestions: false,

      // Buy/Rent MSISDN dialog
      showBuyRentDialog: false,
      selectedBuyRentMsisdnId: null,
      selectedBuyRentMsisdnNumber: null,
      bulkBuyRentMode: false,
      selectedMsisdnIdsForBulkPurchase: [],

      // Payment popup dialog
      showPaymentPopup: false,
      paymentPopupUrl: '',
      paymentPopupMessage: '',

      // Pay button loading state
      payingServiceId: null,

      // Send Request dialog (for non-distributor owned MSISDNs)
      showSendRequestDialog: false,
      sendRequestItem: null,
      sendRequestMessage: '',
      sendRequestLoading: false,

      // Selection
      selectedItems: [],
      selectAllMode: false,
      allFilteredIds: [],
      loadingAllIds: false,

      // Create group dialog
      showCreateGroupDialog: false,
      newGroupName: '',
      newGroupDescription: '',
      createGroupLoading: false,

      // Add to group dialog
      showAddToGroupDialog: false,
      existingMsisdnGroups: [],
      selectedGroupId: null,
      addToGroupLoading: false,

      // Portability dialog
      showPortabilityDialog: false,
      portabilityMsisdn: null,

      // Cache rebuild
      cacheRebuilding: false,

      // User type
      isMultilevelReseller: false,

      // Inline create group in Add to Group dialog
      showInlineCreateForm: false,
      inlineNewGroupName: '',
      inlineNewGroupDescription: '',
      inlineNewGroupClientId: null,
      inlineCreateGroupLoading: false
    }
  },
  computed: {
    msisdnClassesWithAll() {
      return [
        { id: null, name: 'All Classes' },
        ...this.msisdnClasses
      ]
    },
    msisdnGroupsWithAll() {
      return [
        { id: null, name: 'All Groups' },
        ...this.msisdnGroups
      ]
    },
    totalPages() {
      return Math.ceil(this.totalResults / this.perPage)
    },
    productCodesForSelect() {
      return this.availableProductCodes.map(p => ({
        name: p.name,
        display: p.description ? `${p.name} - ${p.description}` : p.name
      }))
    },
    reprovisionProductCodesForSelect() {
      return this.reprovisionProductCodes.map(p => ({
        name: p.name,
        display: p.description ? `${p.name} - ${p.description}` : p.name
      }))
    },
    hasSearchCriteria() {
      if (!this.isMultilevelReseller) return true
      const search = this.filters.msisdn_like ? this.filters.msisdn_like.trim().replace(/\*/g, '') : ''
      return (search.length >= 3) ||
             (this.filters.client_id !== null) ||
             (this.filters.msisdn_class_id !== null) ||
             (this.filters.msisdn_group_id !== null) ||
             (this.filters.network_id !== null) ||
             (['active', 'pending_payment', 'expired', 'suspended', 'terminated', 'refunded', 'available'].includes(this.filters.statusMode))
    }
  },
  watch: {
    // Reset to page 1 when filters change
    'filters.msisdn_like'() {
      this.currentPage = 1;
    },
    'filters.statusMode'() {
      this.currentPage = 1;
    },
    'filters.msisdn_class_id'() {
      this.currentPage = 1;
    },
    'filters.msisdn_group_id'() {
      this.currentPage = 1;
    },
    'filters.include_subclients'() {
      this.currentPage = 1;
    },
    'filters.client_id'(newVal, oldVal) {
      this.currentPage = 1;
      // Reset group filter when client changes (groups are client-specific)
      if (newVal !== oldVal) {
        this.filters.msisdn_group_id = null;
        this.msisdnGroups = [];
      }
    },
    sortBy() {
      this.currentPage = 1;
    }
  },
  methods: {
    async loadMsisdns() {
      if (!this.hasSearchCriteria) {
        this.msisdnsList = []
        this.totalResults = 0
        this.initialLoadDone = false
        return
      }
      log("Loading MSISDNs with filters")
      log("Current statusMode: " + this.filters.statusMode)
      this.loading = true
      this.initialLoadDone = true

      try {
        const [sortField, sortDirection] = this.sortBy.split('_').reduce((acc, part, i, arr) => {
          if (i === arr.length - 1) {
            return [acc[0], part]
          }
          return [acc[0] ? acc[0] + '_' + part : part, acc[1]]
        }, ['', ''])

        // Convert statusMode to statuses array
        let statuses = []
        let statusAll = false
        let excludeTerminated = false
        switch (this.filters.statusMode) {
          case 'all':
            statusAll = true
            break
          case 'all_except_terminated':
            statusAll = true
            excludeTerminated = true
            break
          default:
            statuses = [this.filters.statusMode]
        }

        // Convert letters to digits if in letter search mode
        let searchValue = this.filters.msisdn_like ? this.filters.msisdn_like.trim() : '';
        if (this.searchByLetters && searchValue) {
          searchValue = lettersToDigits(searchValue);
          log("Converted '" + this.filters.msisdn_like + "' to digits: " + searchValue);
        }

        const options = {
          statuses: statuses,
          status_all: statusAll,
          exclude_terminated: excludeTerminated,
          msisdn_class_id: this.filters.msisdn_class_id,
          msisdn_group_id: this.filters.msisdn_group_id,
          network_id: this.filters.network_id,
          msisdn_like: searchValue,
          include_subclients: this.filters.include_subclients,
          client_id: this.filters.client_id || (!this.isMultilevelReseller ? this.client_id : null),
          sort_by: sortField,
          sort_direction: sortDirection,
          page: this.currentPage,
          per_page: this.perPage
        }

        const response = await getAllocatedMsisdns(this.token, options)

        if (!response.error) {
          this.msisdnsList = response.msisdn_list || []
          this.totalResults = response.total_count || 0
          // Update filter options from response
          if (response.filters) {
            this.msisdnGroups = response.filters.msisdn_groups || []
            // Only update classes if not already loaded
            if (this.msisdnClasses.length === 0) {
              this.msisdnClasses = response.filters.msisdn_classes || []
            }
            // Load networks only once
            if (this.networks.length === 0) {
              this.networks = response.filters.networks || []
            }
          }
          log("Loaded " + this.msisdnsList.length + " MSISDNs")
        } else {
          log("Error loading MSISDNs: " + response.message)
        }
      } catch (err) {
        log("Exception loading MSISDNs:")
        log(err)
      } finally {
        this.loading = false
      }
    },

    async loadMsisdnClasses() {
      try {
        const response = await getMsisdnsClasses(this.token)
        if (!response.error) {
          this.msisdnClasses = response.msisdn_classes || []
        }
      } catch (err) {
        log("Error loading MSISDN classes:")
        log(err)
      }
    },
    async loadMsisdnStatuses() {
      try {
        const response = await getMsisdnStatuses(this.token)
        if (!response.error) {
          this.msisdnStatuses = response.msisdn_statuses || []
        }
      } catch (err) {
        log("Error loading MSISDN statuses:")
        log(err)
      }
    },

    resetFilters() {
      this.filters = {
        statusMode: 'all_except_terminated',
        msisdn_like: '',
        msisdn_class_id: null,
        msisdn_group_id: null,
        network_id: null,
        include_subclients: false,
        client_id: null
      }
      this.sortBy = 'expiration_date_asc'
      this.currentPage = 1
      this.msisdnsList = []
      this.totalResults = 0
      this.initialLoadDone = false
    },

    onPageChange(page) {
      this.currentPage = page
      this.loadMsisdns()
    },

    onTableSortChange(sortBy) {
      log("Sort changed:");
      log(sortBy);
      if (sortBy && sortBy.length > 0) {
        const sortItem = sortBy[0];
        const direction = sortItem.order === 'desc' ? 'desc' : 'asc';
        this.sortBy = sortItem.key + '_' + direction;
      } else {
        this.sortBy = 'expiration_date_asc';
      }
      this.loadMsisdns();
    },

    navigateToClient(clientId) {
      if (clientId) {
        this.$router.push({ name: 'clients', query: { client_id: clientId } });
      }
    },

    goToMyMsisdns() {
      this.$router.push('/my-msisdns');
    },

    openBuyNumberDialog() {
      this.showBuyNumberDialog = true
    },

    closeBuyNumberDialog() {
      this.showBuyNumberDialog = false
    },

    onMsisdnPurchased(data) {
      log("MSISDN purchased:")
      log(data)
      this.showBuyNumberDialog = false
      // Reload list to show new number
      this.loadMsisdns()
    },

    goToUnderConstruction(item) {
      this.$router.push({ name: 'underConstruction', query: { msisdn: item.msisdn } })
    },

    goToSim(item) {
      if (item.sim_id) {
        this.$router.push({ name: 'sim', params: { id: item.sim_id } })
      } else {
        this.$router.push({ name: 'sims', query: { search: item.msisdn } })
      }
    },

    loadCredit(item) {
      this.$router.push({ name: 'clientServices', query: { search: item.imsi_serial || item.msisdn } })
    },

    openDetailsPopup(simId, msisdnId) {
      this.selectedSimId = simId
      this.selectedMsisdnId = msisdnId
      this.showDetailsPopup = true
    },

    closeDetailsPopup() {
      this.showDetailsPopup = false
      this.selectedSimId = null
      this.selectedMsisdnId = null
    },

    // Provision methods
    async openProvisionDialog(item) {
      log("openProvisionDialog for item:")
      log(item)
      this.provisioningItem = item
      this.selectedProvisionImsi = null
      this.availableImsisForProvision = []
      this.provisionImsiSearchText = ''
      this.noImsisAvailable = false
      this.imsiLoadAttempted = false
      this.selectedProductCode = null
      this.availableProductCodes = []
      this.showProvisionDialog = true
      // Load available IMSIs and product codes in parallel
      await Promise.all([
        this.loadAvailableImsis(''),
        this.loadProductCodes()
      ])
    },

    closeProvisionDialog() {
      this.showProvisionDialog = false
      this.provisioningItem = null
      this.selectedProvisionImsi = null
      this.availableImsisForProvision = []
      this.provisionImsiSearchText = ''
      this.noImsisAvailable = false
      this.imsiLoadAttempted = false
      this.selectedProductCode = null
      this.availableProductCodes = []
    },

    async loadProductCodes() {
      this.loadingProductCodes = true
      try {
        const response = await getProductCodesForProvisioning(this.token)
        if (!response.error) {
          this.availableProductCodes = response.product_codes || []
          // Auto-select first if only one
          if (this.availableProductCodes.length === 1) {
            this.selectedProductCode = this.availableProductCodes[0].name
          }
        }
      } catch (err) {
        log("Error loading product codes:")
        log(err)
      } finally {
        this.loadingProductCodes = false
      }
    },

    async loadAvailableImsis(searchTerm = '') {
      // Allow empty search to load all available IMSIs
      // Only require 2 chars when user is actively searching
      if (searchTerm && searchTerm.length > 0 && searchTerm.length < 2) {
        return // Don't search with 1 character, wait for more
      }

      this.loadingAvailableImsis = true
      try {
        const simId = this.provisioningItem?.sim_id
        log("Loading available IMSIs for sim_id=" + simId + ", search=" + searchTerm)
        const response = await getAvailableImsisForProvision(this.token, simId, searchTerm)
        if (!response.error) {
          this.availableImsisForProvision = response.imsis || []
          // Track if no IMSIs are available (only on initial load without search)
          if (!searchTerm || searchTerm === '') {
            this.imsiLoadAttempted = true
            this.noImsisAvailable = this.availableImsisForProvision.length === 0
          }
        }
      } catch (err) {
        log("Error loading available IMSIs:")
        log(err)
      } finally {
        this.loadingAvailableImsis = false
      }
    },

    onProvisionImsiSearch(searchText) {
      if (this.provisionSearchTimeout) {
        clearTimeout(this.provisionSearchTimeout)
      }
      this.provisionSearchTimeout = setTimeout(() => {
        this.loadAvailableImsis(searchText)
      }, 300)
    },

    async requestImsisFromDistributor() {
      try {
        const response = await requestImsisFromDistributor(this.token, {
          quantity: 10,
          imsi_type_name: 'standard',
          reason: 'Stoc epuizat - solicitare din provizionare MSISDN',
          urgency: 2,
          importance: 2
        })

        if (!response.error) {
          this.showSnackbarMessage(
            response.message || 'Solicitare trimisa cu succes catre distribuitor!',
            'success'
          )
        } else {
          this.showSnackbarMessage(
            response.message || 'Eroare la trimiterea solicitarii',
            'error'
          )
        }
      } catch (err) {
        log("Error requesting IMSIs:")
        log(err)
        this.showSnackbarMessage('Eroare la trimiterea solicitarii', 'error')
      }
      this.closeProvisionDialog()
    },

    async executeProvision() {
      if (!this.provisioningItem || !this.selectedProvisionImsi || !this.selectedProductCode) return

      this.provisionLoading = true
      try {
        log("Executing provision with sim_id=" + this.provisioningItem.sim_id + ", msisdn_id=" + this.provisioningItem.msisdn_id + ", imsi_id=" + this.selectedProvisionImsi.id + ", mno_product=" + this.selectedProductCode + ", client_service_id=" + this.provisioningItem.client_service_id)
        const response = await provisionSim(
          this.token,
          this.provisioningItem.sim_id,
          this.provisioningItem.msisdn_id,
          this.selectedProvisionImsi.id,
          this.selectedProductCode,
          this.provisioningItem.client_service_id
        )

        if (!response.error) {
          this.showSnackbarMessage(response.message || 'Provizionare reusita!', 'success')
          this.closeProvisionDialog()
          this.loadMsisdns()
        } else {
          this.showSnackbarMessage(response.message || 'Eroare la provizionare', 'error')
        }
      } catch (err) {
        log("Error executing provision:")
        log(err)
        this.showSnackbarMessage('Eroare la provizionare', 'error')
      } finally {
        this.provisionLoading = false
      }
    },

    // Reprovision methods (for terminated SIMs)
    async openReprovisionDialog(item) {
      log("openReprovisionDialog for item:")
      log(item)
      this.reprovisionItem = item
      this.selectedReprovisionImsi = null
      this.availableReprovisionImsis = []
      this.reprovisionImsiSearchText = ''
      this.noReprovisionImsisAvailable = false
      this.reprovisionImsiLoadAttempted = false
      this.selectedReprovisionProductCode = null
      this.reprovisionProductCodes = []
      this.showReprovisionDialog = true
      // Load available IMSIs and product codes in parallel
      await Promise.all([
        this.loadReprovisionImsis(''),
        this.loadReprovisionProductCodes()
      ])
      // Auto-select the same IMSI if it's available
      if (item.imsi_id && this.availableReprovisionImsis.length > 0) {
        const sameImsi = this.availableReprovisionImsis.find(i => i.id === item.imsi_id)
        if (sameImsi) {
          this.selectedReprovisionImsi = sameImsi
          log("Auto-selected same IMSI: " + sameImsi.id)
        }
      }
    },

    closeReprovisionDialog() {
      this.showReprovisionDialog = false
      this.reprovisionItem = null
      this.selectedReprovisionImsi = null
      this.availableReprovisionImsis = []
      this.reprovisionImsiSearchText = ''
      this.noReprovisionImsisAvailable = false
      this.reprovisionImsiLoadAttempted = false
      this.selectedReprovisionProductCode = null
      this.reprovisionProductCodes = []
    },

    async loadReprovisionProductCodes() {
      this.loadingReprovisionProductCodes = true
      try {
        const response = await getProductCodesForProvisioning(this.token)
        if (!response.error) {
          this.reprovisionProductCodes = response.product_codes || []
          // Auto-select first if only one
          if (this.reprovisionProductCodes.length === 1) {
            this.selectedReprovisionProductCode = this.reprovisionProductCodes[0].name
          }
        }
      } catch (err) {
        log("Error loading reprovision product codes:")
        log(err)
      } finally {
        this.loadingReprovisionProductCodes = false
      }
    },

    async loadReprovisionImsis(searchTerm = '') {
      if (searchTerm && searchTerm.length > 0 && searchTerm.length < 2) {
        return
      }

      this.loadingReprovisionImsis = true
      try {
        // Use the client_id from the reprovision item to filter IMSIs by client
        const clientId = this.reprovisionItem?.client_id
        log("Loading available IMSIs for client_id=" + clientId + ", search=" + searchTerm)
        const response = await getAvailableImsisForProvision(this.token, null, searchTerm, clientId)
        if (!response.error) {
          this.availableReprovisionImsis = response.imsis || []
          if (!searchTerm || searchTerm === '') {
            this.reprovisionImsiLoadAttempted = true
            this.noReprovisionImsisAvailable = this.availableReprovisionImsis.length === 0
          }
        }
      } catch (err) {
        log("Error loading reprovision IMSIs:")
        log(err)
      } finally {
        this.loadingReprovisionImsis = false
      }
    },

    onReprovisionImsiSearch(searchText) {
      if (this.reprovisionSearchTimeout) {
        clearTimeout(this.reprovisionSearchTimeout)
      }
      this.reprovisionSearchTimeout = setTimeout(() => {
        this.loadReprovisionImsis(searchText)
      }, 300)
    },

    async executeReprovision() {
      if (!this.reprovisionItem || !this.selectedReprovisionImsi || !this.selectedReprovisionProductCode) return

      this.reprovisionLoading = true
      try {
        log("Executing reprovision with imsi_id=" + this.selectedReprovisionImsi.id + ", msisdn_id=" + this.reprovisionItem.msisdn_id + ", mno_product=" + this.selectedReprovisionProductCode)
        const response = await reprovisionTerminatedSim(
          this.token,
          this.selectedReprovisionImsi.id,
          this.reprovisionItem.msisdn_id,
          this.selectedReprovisionProductCode
        )

        if (!response.error) {
          this.showSnackbarMessage(response.message || 'Reprovizionare reusita!', 'success')
          this.closeReprovisionDialog()
          this.loadMsisdns()
        } else {
          this.showSnackbarMessage(response.message || 'Eroare la reprovizionare', 'error')
        }
      } catch (err) {
        log("Error executing reprovision:")
        log(err)
        this.showSnackbarMessage('Eroare la reprovizionare', 'error')
      } finally {
        this.reprovisionLoading = false
      }
    },

    // Delete service methods
    confirmDeleteService(item) {
      this.deletingItem = item
      this.showDeleteDialog = true
    },

    async executeDeleteService() {
      if (!this.deletingItem) return

      this.deleteLoading = true
      try {
        const response = await deleteClientService(
          this.token,
          this.deletingItem.client_service_id
        )

        if (!response.error) {
          this.showSnackbarMessage(response.message || 'Serviciu sters cu succes!', 'success')
          this.showDeleteDialog = false
          this.deletingItem = null
          this.loadMsisdns()
        } else {
          this.showSnackbarMessage(response.message || 'Eroare la stergere', 'error')
        }
      } catch (err) {
        log("Error deleting service:")
        log(err)
        this.showSnackbarMessage('Eroare la stergere', 'error')
      } finally {
        this.deleteLoading = false
      }
    },

    // Check if item has active CS for msisdn or imsi_msisdn service
    hasActiveMsisdnService(item) {
      const msisdnServiceTypes = ['msisdn', 'imsi_msisdn'];
      return item.status_name === 'active' &&
             item.service_type_name &&
             msisdnServiceTypes.includes(item.service_type_name.toLowerCase());
    },

    // Terminate service methods
    confirmTerminateService(item) {
      this.terminatingItem = item;
      this.showTerminateDialog = true;
    },

    async executeTerminateService() {
      if (!this.terminatingItem) return;

      this.terminateLoading = true;
      try {
        const response = await terminateClientService(
          this.token,
          this.terminatingItem.client_service_id
        );

        if (!response.error) {
          this.showSnackbarMessage(response.message || 'Serviciu terminat cu succes!', 'success');
          this.showTerminateDialog = false;
          this.terminatingItem = null;
          this.loadMsisdns();
        } else {
          this.showSnackbarMessage(response.message || 'Eroare la terminare', 'error');
        }
      } catch (err) {
        log("Error terminating service:");
        log(err);
        this.showSnackbarMessage('Eroare la terminare', 'error');
      } finally {
        this.terminateLoading = false;
      }
    },

    showSnackbarMessage(message, color) {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.showSnackbar = true
    },

    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'pending_payment': 'info',
        'expired': 'warning',
        'suspended': 'orange',
        'terminated': 'error',
        'refunded': 'grey',
        'available': 'blue-grey'
      }
      return colors[status] || 'grey'
    },

    getMsisdnStatusColor(status) {
      const colors = {
        'available': 'success',
        'reserved': 'info',
        'allocated': 'purple',
        'not_available': 'error',
        'quarantine': 'orange',
        'pending payment': 'warning',
        'expired': 'grey'
      }
      return colors[status] || 'blue-grey'
    },

    getSimStatusColor(status) {
      const colors = {
        'active': 'success',
        'suspended': 'warning',
        'not_provisioned': 'info',
        'available': 'blue-grey'
      }
      return colors[status] || 'grey'
    },

    formatSimStatus(status) {
      const labels = {
        'active': 'Active',
        'suspended': 'Suspended',
        'not_provisioned': 'Not Provisioned',
        'available': 'Available'
      }
      return labels[status] || status || '-'
    },

    getRowProps({ item }) {
      // Green background if sim_status is active or suspended
      if (item.sim_status_name === 'active' || item.sim_status_name === 'suspended') {
        return { class: 'row-sim-active' }
      }
      if (item.status_name === 'expired') {
        return { class: 'row-expired' }
      }
      if (item.status_name === 'terminated') {
        return { class: 'row-terminated' }
      }
      return {}
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    },

    isExpiringSoon(dateString) {
      if (!dateString) return false
      const expirationDate = new Date(dateString)
      const now = new Date()
      const daysUntilExpiration = (expirationDate - now) / (1000 * 60 * 60 * 24)
      return daysUntilExpiration <= 7 && daysUntilExpiration > 0
    },

    // ==================== Selection Methods ====================

    async selectAllFilteredMsisdns() {
      this.loadingAllIds = true
      try {
        const filters = {
          status_mode: this.filters.statusMode,
          search_term: this.filters.msisdn_like ? this.filters.msisdn_like.trim() : '',
          client_id: this.filters.client_id,
          include_subclients: this.filters.include_subclients
        }
        const response = await getAllFilteredMsisdnIds(this.token, filters)
        if (!response.error) {
          this.allFilteredIds = response.msisdn_ids || []
          this.selectedItems = [...this.allFilteredIds]
          this.selectAllMode = true
        } else {
          this.showSnackbarMessage(response.message || 'Error loading all IDs', 'error')
        }
      } catch (err) {
        log("Error in selectAllFilteredMsisdns:", err)
        this.showSnackbarMessage('Error loading all IDs', 'error')
      } finally {
        this.loadingAllIds = false
      }
    },

    clearSelection() {
      this.selectedItems = []
      this.selectAllMode = false
      this.allFilteredIds = []
    },

    // ==================== Group Methods ====================

    openCreateGroupDialog() {
      this.newGroupName = ''
      this.newGroupDescription = ''
      this.showCreateGroupDialog = true
    },

    async createMsisdnGroupAction() {
      if (!this.newGroupName) return
      this.createGroupLoading = true
      try {
        const msisdnIds = this.selectAllMode ? this.allFilteredIds : this.selectedItems
        const response = await createMsisdnGroup(this.token, this.newGroupName, this.newGroupDescription, msisdnIds)
        if (!response.error) {
          this.showSnackbarMessage(`Group "${this.newGroupName}" created with ${response.group?.member_count || 0} members`, 'success')
          this.showCreateGroupDialog = false
          this.clearSelection()
        } else {
          this.showSnackbarMessage(response.message || 'Error creating group', 'error')
        }
      } catch (err) {
        log("Error creating group:", err)
        this.showSnackbarMessage('Error creating group', 'error')
      } finally {
        this.createGroupLoading = false
      }
    },

    async openAddToGroupDialog() {
      this.selectedGroupId = null
      this.showInlineCreateForm = false
      this.inlineNewGroupName = ''
      this.inlineNewGroupDescription = ''
      this.inlineNewGroupClientId = null
      this.showAddToGroupDialog = true
      try {
        const response = await getMsisdnGroups(this.token)
        if (!response.error) {
          this.existingMsisdnGroups = response.groups || []
        }
      } catch (err) {
        log("Error loading groups:", err)
      }
    },

    closeAddToGroupDialog() {
      this.showAddToGroupDialog = false
      this.showInlineCreateForm = false
      this.selectedGroupId = null
      this.inlineNewGroupName = ''
      this.inlineNewGroupDescription = ''
      this.inlineNewGroupClientId = null
    },

    async inlineCreateGroupAndAdd() {
      if (!this.inlineNewGroupName) return
      this.inlineCreateGroupLoading = true
      try {
        const msisdnIds = this.selectAllMode ? this.allFilteredIds : this.selectedItems
        const response = await createMsisdnGroup(
          this.token,
          this.inlineNewGroupName,
          this.inlineNewGroupDescription,
          msisdnIds,
          this.inlineNewGroupClientId
        )
        if (!response.error) {
          this.showSnackbarMessage(`Group "${this.inlineNewGroupName}" created with ${response.group?.member_count || 0} members`, 'success')
          this.closeAddToGroupDialog()
          this.clearSelection()
        } else {
          this.showSnackbarMessage(response.message || 'Error creating group', 'error')
        }
      } catch (err) {
        log("Error creating group inline:", err)
        this.showSnackbarMessage('Error creating group', 'error')
      } finally {
        this.inlineCreateGroupLoading = false
      }
    },

    async addToMsisdnGroup() {
      if (!this.selectedGroupId) return
      this.addToGroupLoading = true
      try {
        const msisdnIds = this.selectAllMode ? this.allFilteredIds : this.selectedItems
        const response = await addMsisdnsToGroup(this.token, this.selectedGroupId, msisdnIds)
        if (!response.error) {
          this.showSnackbarMessage(response.message || 'Added to group', 'success')
          this.showAddToGroupDialog = false
          this.clearSelection()
        } else {
          this.showSnackbarMessage(response.message || 'Error adding to group', 'error')
        }
      } catch (err) {
        log("Error adding to group:", err)
        this.showSnackbarMessage('Error adding to group', 'error')
      } finally {
        this.addToGroupLoading = false
      }
    },

    async rebuildCache() {
      this.cacheRebuilding = true
      try {
        const { rebuildHierarchyCache } = await import('@/composables/useHierarchyCacheAPI')
        const result = await rebuildHierarchyCache(this.token)
        if (!result.error) {
          this.showSnackbarMessage('Cache rebuild started successfully', 'success')
        } else {
          this.showSnackbarMessage(result.message || 'Error rebuilding cache', 'error')
        }
      } catch (e) {
        this.showSnackbarMessage('Error rebuilding cache', 'error')
      } finally {
        this.cacheRebuilding = false
      }
    },

    exportCSV() {
      if (this.msisdnsList.length === 0) return

      const headers = ['MSISDN', 'Status', 'Class', 'Client', 'Expiration Date']
      const rows = this.msisdnsList.map(item => [
        item.msisdn,
        item.status_name || '',
        item.msisdn_class_name || '',
        item.client_name || '',
        item.expiration_date || ''
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `msisdns_export_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // Vanity search methods
    lettersToDigits(input) {
      return lettersToDigits(input);
    },

    async onVanitySearchInput(value) {
      if (!this.searchByLetters || !value || value.length < 2) {
        this.vanitySuggestions = [];
        this.showVanitySuggestions = false;
        return;
      }

      // Debounce the search
      if (this.vanitySearchTimeout) {
        clearTimeout(this.vanitySearchTimeout);
      }

      this.vanitySearchTimeout = setTimeout(async () => {
        this.loadingVanitySuggestions = true;
        try {
          const suggestions = await searchVanityWords(value, 15);
          this.vanitySuggestions = suggestions;
          this.showVanitySuggestions = suggestions.length > 0;
          log("Vanity suggestions:");
          log(suggestions);
        } catch (err) {
          log("Error searching vanity words:");
          log(err);
          this.vanitySuggestions = [];
        } finally {
          this.loadingVanitySuggestions = false;
        }
      }, 300);
    },

    selectVanitySuggestion(suggestion) {
      // Set the search field to the word
      this.filters.msisdn_like = suggestion.word;
      this.showVanitySuggestions = false;
      // Trigger search with the digit equivalent
      this.loadMsisdns();
    },

    closeVanitySuggestions() {
      // Small delay to allow click on suggestion
      setTimeout(() => {
        this.showVanitySuggestions = false;
      }, 200);
    },

    // Buy/Rent MSISDN dialog methods
    openBuyRentDialog(item) {
      log("openBuyRentDialog called");
      log(item);
      this.bulkBuyRentMode = false;
      this.selectedBuyRentMsisdnId = item.msisdn_id || item.id;
      this.selectedBuyRentMsisdnNumber = item.msisdn_number || item.msisdn;
      this.showBuyRentDialog = true;
    },

    openBulkBuyRentDialog() {
      log("openBulkBuyRentDialog called");
      log("Selected items: " + this.selectedItems.length);
      if (this.selectedItems.length === 0) {
        this.showSnackbarMessage('Please select at least one MSISDN', 'warning');
        return;
      }
      this.bulkBuyRentMode = true;
      this.selectedMsisdnIdsForBulkPurchase = this.selectAllMode ? this.allFilteredIds : [...this.selectedItems];
      this.showBuyRentDialog = true;
    },

    closeBuyRentDialog() {
      this.showBuyRentDialog = false;
      this.bulkBuyRentMode = false;
      this.selectedBuyRentMsisdnId = null;
      this.selectedBuyRentMsisdnNumber = null;
      this.selectedMsisdnIdsForBulkPurchase = [];
    },

    onBuyRentPurchased(data) {
      log("MSISDN purchased via Buy/Rent dialog:");
      log(data);
      this.closeBuyRentDialog();
      this.showSnackbarMessage(data.message || 'Order created successfully!', 'success');
      // Refresh the list
      this.loadMsisdns();
    },

    onBulkBuyRentPurchased(data) {
      log("Bulk MSISDN purchase started:");
      log(data);
      this.closeBuyRentDialog();
      this.clearSelection();
      this.showSnackbarMessage(data.message || 'Bulk purchase started. Check notifications for results.', 'info');
    },

    openPortabilityDialog(item) {
      this.portabilityMsisdn = {
        msisdn: item.msisdn,
        msisdn_id: item.msisdn_id,
        verified_at: true
      };
      this.showPortabilityDialog = true;
    },

    onPortRequestCreated() {
      this.showPortabilityDialog = false;
      this.showSnackbarMessage('Cerere de portabilitate trimisa cu succes!', 'success');
      this.loadMsisdns();
    },

    handlePaymentComplete(event) {
      log("Payment complete event received:");
      log(event.detail);
      // Refresh the list when payment is complete
      this.resetFilters();
    },

    showPaymentDialog(url, message) {
      this.paymentPopupUrl = url;
      this.paymentPopupMessage = message || 'Click the button below to complete your payment.';
      this.showPaymentPopup = true;
    },

    onPaymentPopupPayClick() {
      // Close dialog after clicking Pay (URL opens in new tab via href)
      this.showPaymentPopup = false;
      // Reset filters after a short delay
      setTimeout(() => {
        this.resetFilters();
      }, 300);
    },

    closePaymentPopup() {
      this.showPaymentPopup = false;
      this.resetFilters();
    },

    handleShowPaymentPopup(event) {
      if (event.detail && event.detail.url) {
        this.showPaymentDialog(event.detail.url, event.detail.message);
      }
    },

    async payForService(item) {
      log("payForService called for client_service_id: " + item.client_service_id);
      this.payingServiceId = item.client_service_id;

      try {
        const response = await payOrder(this.token, item.client_service_id);

        if (!response.error && response.btUrl) {
          // Open the BT payment URL in a new tab
          window.open(response.btUrl, '_blank');
          this.showSnackbarMessage('Payment page opened', 'success');
        } else {
          this.showSnackbarMessage(response.message || 'Failed to initiate payment', 'error');
        }
      } catch (err) {
        log("Error in payForService:");
        log(err);
        this.showSnackbarMessage('Error initiating payment', 'error');
      } finally {
        this.payingServiceId = null;
      }
    },

    // Send Request methods (for non-distributor owned MSISDNs)
    openSendRequestDialog(item) {
      log("openSendRequestDialog for item:");
      log(item);
      this.sendRequestItem = item;
      this.sendRequestMessage = '';
      this.showSendRequestDialog = true;
    },

    closeSendRequestDialog() {
      this.showSendRequestDialog = false;
      this.sendRequestItem = null;
      this.sendRequestMessage = '';
    },

    async executeSendRequest() {
      if (!this.sendRequestItem) return;

      this.sendRequestLoading = true;
      try {
        const response = await sendMsisdnInterestRequest(
          this.token,
          this.sendRequestItem.msisdn_id,
          this.sendRequestMessage
        );

        if (!response.error) {
          this.showSnackbarMessage(response.message || 'Solicitare trimisa cu succes!', 'success');
          this.closeSendRequestDialog();
        } else {
          this.showSnackbarMessage(response.message || 'Eroare la trimiterea solicitarii', 'error');
        }
      } catch (err) {
        log("Error sending interest request:");
        log(err);
        this.showSnackbarMessage('Eroare la trimiterea solicitarii', 'error');
      } finally {
        this.sendRequestLoading = false;
      }
    }
  },

  created() {
    const {getLoggedIn, user, loggedIn, token} = useGetLoggedIn()
    getLoggedIn().then(async () => {
      log("MsisdnsList after getLoggedIn")
      log("user:")
      log(user)
      log("loggedIn: " + loggedIn.value)
      log("token: " + token.value)
      this.token = store.getters.getToken
      this.client_id = user.value?.client_id

      // Detect multilevel reseller (keep 3-char search restriction only for them)
      const clientTypeName = user.value?.client_type_name?.toLowerCase() || ''
      this.isMultilevelReseller = clientTypeName === 'multilevel'

      // Load display settings for perPage
      let ds = store.state.displaySettings
      if (!ds || !ds.default_page_size_msisdns) {
        const dsResult = await getDisplaySettings(this.token)
        if (!dsResult.error && dsResult.display_settings) {
          ds = dsResult.display_settings
          store.commit('setDisplaySettings', ds)
        }
      }
      if (ds && ds.default_page_size_msisdns) {
        this.perPage = ds.default_page_size_msisdns
      }

      // Check for search query parameter
      const searchParam = this.$route.query.search
      if (searchParam) {
        this.filters.msisdn_like = searchParam
      }

      // Load MSISDN classes and statuses for filter dropdowns
      this.loadMsisdnClasses()
      this.loadMsisdnStatuses()

      // Only load MSISDNs if a search param was provided (don't load all on page open)
      if (searchParam && searchParam.length >= 3) {
        this.loadMsisdns()
      }
    })
  },

  mounted() {
    // Listen for payment complete events from payment popup
    window.addEventListener('payment-complete', this.handlePaymentComplete);
    // Listen for show payment popup event from notifications panel
    window.addEventListener('show-payment-popup', this.handleShowPaymentPopup);
  },

  beforeUnmount() {
    window.removeEventListener('payment-complete', this.handlePaymentComplete);
    window.removeEventListener('show-payment-popup', this.handleShowPaymentPopup);
  }
});
</script>

<style scoped>
.clickable-link {
  cursor: pointer;
}
.gap-2 {
  gap: 8px;
}

.selection-toolbar {
  position: sticky;
  top: 64px; /* Account for navbar height */
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.position-relative {
  position: relative;
}

.clickable-client {
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
}

.clickable-client:hover {
  color: #1565c0;
}

.vanity-suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
}

.vanity-suggestion-item {
  cursor: pointer;
}

.vanity-suggestion-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.1);
}
</style>

<style>
/* Row styles for data table - not scoped to apply to Vuetify components */
.v-data-table .row-sim-active {
  background-color: rgba(76, 175, 80, 0.10) !important;
}

.v-data-table .row-sim-active td {
  color: inherit !important;
}

.v-data-table .row-expired {
  background-color: #d32f2f !important;
  color: white !important;
}

.v-data-table .row-expired td {
  color: white !important;
}

.v-data-table .row-terminated {
  background-color: #9e9e9e !important;
  color: white !important;
}

.v-data-table .row-terminated td {
  color: white !important;
}
</style>
